if (!customElements.get('product-description')) {
  customElements.define(
    'product-description',
    class ProductDescription extends HTMLElement {
      constructor() {
        super();

        this.content = this.querySelector('[data-collapsible-content]');
        this.toggle = this.querySelector('button');
        this.onToggleClick = this.onToggleClick.bind(this);
        this.onTransitionEnd = this.onTransitionEnd.bind(this);
      }

      connectedCallback() {
        if (!this.content || !this.toggle) return;

        this.toggle.addEventListener('click', this.onToggleClick);
        this.content.addEventListener('transitionend', this.onTransitionEnd);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.setInitialState());
          this.resizeObserver.observe(this.content);
        }

        this.setInitialState();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggleClick);
        this.content?.removeEventListener('transitionend', this.onTransitionEnd);
        this.resizeObserver?.disconnect();
      }

      get collapsedHeight() {
        const styles = getComputedStyle(this);
        const collapsedHeightValue = styles.getPropertyValue('--product-description-collapsed-height').trim();
        const collapsedHeight = parseFloat(collapsedHeightValue);

        if (collapsedHeightValue.endsWith('rem')) {
          return collapsedHeight * parseFloat(getComputedStyle(document.documentElement).fontSize);
        }

        return collapsedHeight;
      }

      setInitialState() {
        if (!this.isConnected || !this.content || !this.toggle) return;
        if (this.classList.contains('is-animating')) return;

        const fullHeight = this.content.scrollHeight;
        const isCollapsible = fullHeight > this.collapsedHeight + 4;

        if (!isCollapsible) {
          this.classList.add('is-expanded');
          this.content.style.height = 'auto';
          this.toggle.hidden = true;
          this.toggle.setAttribute('aria-expanded', 'true');
          return;
        }

        this.toggle.hidden = false;

        if (!this.classList.contains('is-expanded')) {
          this.content.style.height = `${this.collapsedHeight}px`;
          this.toggle.setAttribute('aria-expanded', 'false');
          return;
        }

        this.content.style.height = 'auto';
        this.toggle.setAttribute('aria-expanded', 'true');
      }

      onToggleClick() {
        if (this.classList.contains('is-animating')) return;

        const expanded = this.classList.contains('is-expanded');
        const startHeight = expanded ? this.content.scrollHeight : this.collapsedHeight;
        const endHeight = expanded ? this.collapsedHeight : this.content.scrollHeight;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
          this.classList.toggle('is-expanded', !expanded);
          this.toggle.setAttribute('aria-expanded', String(!expanded));
          this.content.style.height = !expanded ? 'auto' : `${this.collapsedHeight}px`;
          return;
        }

        this.classList.add('is-animating');
        this.content.style.height = `${startHeight}px`;
        this.content.offsetHeight;

        requestAnimationFrame(() => {
          this.classList.toggle('is-expanded', !expanded);
          this.toggle.setAttribute('aria-expanded', String(!expanded));
          this.content.style.height = `${endHeight}px`;
        });
      }

      onTransitionEnd(event) {
        if (event.propertyName !== 'height') return;

        this.classList.remove('is-animating');

        if (this.classList.contains('is-expanded')) {
          this.content.style.height = 'auto';
          return;
        }

        this.content.style.height = `${this.collapsedHeight}px`;
      }
    }
  );
}
